/**
 * useChatOnboarding
 *
 * Chat-window onboarding orchestration (replaces the card-on-wood recipe host).
 * Run from inside ChatBox while `onboardingActive` is true and the onboarding
 * (Welcome) conversation is the active thread. It:
 *
 *   1. Seeds "Welcome to XAVIOR." into the REAL chat thread (no user turn
 *      required) via `addAssistantMessage`, immediately.
 *   2. After a brief beat, seeds "Look around, or ask me anything." — display-only,
 *      and does NOT wait on data readiness (#5387): the user goes straight from the
 *      welcome to the invitation, then explores/asks freely while email/calendar
 *      sync continues silently in the background. There are no suggestion chips,
 *      and no visible "pulling in your email" beat.
 *   3. Independently, polls the existing `/workspaces/:id/onboarding/sync-status`
 *      endpoint for phase-1 readiness (`isReady`) and GATES the TOUR FINALE
 *      (composer unlock + completion latch) on it (#5225): the finale fires no
 *      earlier than a short floor (so the opening beats can land) and no later
 *      than a hard cap (so the user is never trapped), but between the two it
 *      waits for REAL data — a first mail sync lands ~60–90s after connect. The
 *      finale happens silently (no status line). The RAIL is not part of this
 *      gate — it loads with the chat at shell-readiness (MainLayout / App.tsx gate 5).
 *   4. On ready (or the cap): calls `endOnboardingActive()` (ends the tour —
 *      unlocks the composer; see OnboardingActiveContext / MainLayout), then
 *      latches completion (`completeOnboardingSession` + session `refresh()`) so
 *      `profile.onboardingComplete` flips true and the gate releases cleanly with
 *      no reload. The look-around invite is already on screen by this point
 *      (step 2) — the finale no longer gates it.
 *   5. After the reveal, polls `/workspaces/:id/onboarding/discovery-status`
 *      for the background reveal-letter job and seeds the personalized
 *      `revealText` as an onboarding-routed assistant message when it lands
 *      (#5226). Terminal-without-a-letter outcomes (failed / deferred /
 *      empty-account fallback) seed NOTHING — honest silence.
 *
 * ── REMOUNT SURVIVAL (the core correctness property) ──
 * ChatBox REMOUNTS during onboarding: the App gate chain (App.tsx
 * `AuthenticatedAppShell`) and ConversationRouteSync churn the chat subtree as
 * the auto-created "Welcome" conversation loads/syncs — a held gate returns the
 * pulse loader, then releases to MainLayout, mounting ChatBox fresh; mid-flow
 * detail/active-conversation refetch can re-engage a gate and unmount it again.
 * Each remount gives the hook FRESH refs, so any progress/timer state stored in
 * per-mount refs is lost: the old design re-seeded "Welcome" every mount and its
 * unmount cleanup killed the scheduled 2nd-message / reveal timers before they
 * fired, stranding the flow on "Welcome" (and double-seeding it).
 *
 * Fix: ALL orchestration state — the one-shot progress flags (welcomeSeeded,
 * lookAroundSeeded, finalized) AND the timer/poll handles — live in a MODULE-LEVEL
 * singleton keyed by conversationId (`sessions` below), which is wholly outside
 * the React tree and survives any number of ChatBox mounts/unmounts. The hook is
 * now a thin binder: on each mount it (re)points the running session at the
 * latest callbacks (addAssistantMessage / endOnboardingActive / refreshSession /
 * token / workspaceId) and starts the session ONCE; it NEVER tears the session
 * down on unmount. Idempotency is by ACTUAL recorded progress, not per-instance
 * refs — so a remount can neither re-seed "Welcome" nor lose the pending
 * 2nd-message/reveal steps.
 */
import { useEffect, useRef } from 'react'
import { onboarding } from '@/lib/api/endpoints/onboarding'
import type { DiscoveryStatus } from '@/lib/api/endpoints/onboarding'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'
import type { OnboardingRecipe } from '@/features/onboarding/onboarding-recipes'

const ROUTING = 'onboarding'

// Beat between the welcome line and the "Look around, or ask me anything."
// invite. Display-only — does not wait on sync (#5387).
const SECOND_MESSAGE_DELAY_MS = 900
// Floor: the EARLIEST the SIDEBAR REVEAL may fire, even when sync is already
// ready — the opening beats need a moment to land before the sidebar animates
// in. Does not gate the look-around invite (it fires at the beat above,
// independent of this floor — #5387).
const REVEAL_FLOOR_MS = 4000
// Hard cap: the LATEST the reveal may fire, even if sync readiness never
// arrives — never trap the user in the full-width chat (#5225). A real first
// mail sync lands ~60–90s after connect (the backend deliberately delays the
// initial trigger ~25s), so the cap must cover that window; between the floor
// and the cap the reveal waits for actual readiness.
const REVEAL_CAP_MS = 90_000
// Sync-status poll cadence (the discovery-letter poll reuses it).
const POLL_INTERVAL_MS = 1500
// Stop polling discovery-status for the reveal letter after this long. The
// letter job normally completes a few seconds after the first mail sync; if it
// has not landed by now it is not coming this run — honest silence (#5226).
const LETTER_POLL_TIMEOUT_MS = 180_000

/**
 * Live callbacks the singleton calls. ChatBox re-supplies these on every mount
 * (its closures capture fresh state each render), so the session always invokes
 * the CURRENT instance's functions — never a stale closure from a prior mount.
 */
interface OnboardingBindings {
  token: string | null
  workspaceId: string
  /**
   * Context-selected onboarding recipe (welcome + invite copy). Resolved once
   * in ChatBox and repointed on each mount; the seed steps read it at fire time so
   * a recipe that resolved after a remount is honored. Stable for a given run.
   */
  recipe: OnboardingRecipe
  addAssistantMessage: (
    content: string,
    conversationId?: string,
    metadata?: { routing?: string; durationMs?: number; intent?: string },
  ) => void
  /**
   * True if an onboarding-routed message with EXACTLY `content` is already in
   * the loaded thread. Lets seeding be idempotent against ACTUAL thread
   * contents (not just module-level session state), so an HMR module-reset
   * mid-flow or a hard reload — both of which lose the session registry but
   * leave the already-persisted messages in the reloaded thread — cannot
   * re-seed a duplicate. Repointed every render so it always reflects the
   * current `messages` array.
   */
  hasOnboardingMessage: (content: string) => boolean
  endOnboardingActive: () => void
  refreshSession: () => Promise<void> | void
  /**
   * The conversation currently on screen (ref-backed in ChatBox, so it stays
   * correct even when read from a poll scheduled on an earlier mount). The
   * letter seed is HELD while a different thread is active: addAssistantMessage
   * appends to the live view as well as persisting, so seeding then would paint
   * the letter into the wrong conversation's UI (#5226).
   */
  getActiveConversationId: () => string | null | undefined
}

/**
 * One running onboarding orchestration, keyed by conversationId. Progress flags
 * make every step idempotent by ACTUAL progress; timers/poll live here so they
 * outlive ChatBox mounts. `bindings` is repointed on each mount.
 */
interface OnboardingSession {
  conversationId: string
  bindings: OnboardingBindings
  welcomeSeeded: boolean
  /** True once the "Look around, or ask me anything." invite has been seeded —
   *  fires on the short beat after welcome, independent of sync readiness
   *  (#5387). */
  lookAroundSeeded: boolean
  finalized: boolean
  /** True once the reveal floor has elapsed — the reveal never fires before it. */
  floorElapsed: boolean
  /** True once /sync-status has reported isReady (or there is nothing to poll). */
  syncReady: boolean
  /** True once the reveal letter has been seeded (belt for the content dedupe). */
  letterSeeded: boolean
  timers: ReturnType<typeof setTimeout>[]
  poll: ReturnType<typeof setInterval> | null
}

// Module-level registry — OUTSIDE the React tree, so it survives every ChatBox
// remount. Keyed by conversationId: the welcome thread is one-shot, so at most
// one live session matters, but keying defends against a conversationId change
// mid-flow (we only ever start the session for the active welcome thread).
const sessions = new Map<string, OnboardingSession>()

// Synchronous, cross-session guard for which onboarding lines have been seeded
// this run, keyed by message content. The welcome line is seeded at 0ms by
// startOnboardingSession; if a conversationId change at start spawns a SECOND
// session, both seed the welcome synchronously before React flushes setMessages,
// so the async `hasOnboardingMessage` check (which reads the messages array)
// can't catch the dup — only this synchronous set can. (The delayed look-around
// seed at +900ms has time for the array to update, but it routes through here
// too for consistency.) Cleared when the LAST session
// disposes, so a fresh onboarding in the same tab starts clean.
const seededOnboardingContent = new Set<string>()

function clearTimers(session: OnboardingSession): void {
  session.timers.forEach(clearTimeout)
  session.timers = []
  if (session.poll) {
    clearInterval(session.poll)
    session.poll = null
  }
}

/** Tear down a finished/abandoned session and drop it from the registry. */
function disposeSession(conversationId: string): void {
  const session = sessions.get(conversationId)
  if (!session) return
  clearTimers(session)
  sessions.delete(conversationId)
  // Last session out clears the seed guard so a new onboarding starts clean.
  // Deferring to the empty-map condition means a second session spawned by a
  // conversationId change can't clear the guard out from under the first.
  if (sessions.size === 0) seededOnboardingContent.clear()
}

/**
 * Seed an onboarding line exactly once for the whole run. The synchronous
 * `seededOnboardingContent` guard survives both the async messages-array gap and
 * a conversationId change spawning a second session (the 0ms welcome is the case
 * the async `hasOnboardingMessage` check misses). `hasOnboardingMessage` still
 * guards against a copy already persisted in a reloaded thread (HMR / hard
 * reload), so we don't re-add a line the server already returned.
 */
function seedOnboardingMessage(session: OnboardingSession, content: string): void {
  if (seededOnboardingContent.has(content)) return
  seededOnboardingContent.add(content)
  if (session.bindings.hasOnboardingMessage(content)) return
  session.bindings.addAssistantMessage(content, session.conversationId, {
    routing: ROUTING,
  })
}

/**
 * Seed the "Look around, or ask me anything." invite (#5387). Fires on the
 * short beat after welcome — display-only, independent
 * of sync readiness: the user goes straight from the welcome to the
 * invitation while email/calendar sync continues silently in the background.
 * Idempotent (also called defensively from `finalize` in case the beat timer
 * was somehow cleared before it fired), so it is always seeded exactly once.
 */
function seedLookAround(session: OnboardingSession): void {
  if (session.lookAroundSeeded) return
  session.lookAroundSeeded = true
  seedOnboardingMessage(session, session.bindings.recipe.lookAround)
  // No suggestion chips: onboarding is just the welcome + invite; the user
  // explores/asks freely from here.
}

/**
 * The reveal gate (#5225): fire only once BOTH the floor has elapsed AND sync
 * reported ready. The cap timer is the single caller that bypasses readiness
 * so the user is never trapped.
 */
function maybeFinalize(session: OnboardingSession): void {
  if (!session.floorElapsed || !session.syncReady) return
  finalize(session)
}

function finalize(session: OnboardingSession): void {
  if (session.finalized) return
  session.finalized = true
  clearTimers(session)

  const { bindings } = session
  const { token } = bindings

  // 1) End the tour: unlocks the composer + stops the onboarding beat binding.
  // The rail is NOT revealed here — it loaded together with the chat at
  // shell-readiness (see MainLayout / App.tsx gate 5). The look-around invite
  // is NOT gated on this either (#5387) — it was already seeded on the short
  // post-welcome beat (seedLookAround), independent of sync readiness.
  // Defensive belt: if that beat was somehow cleared before it fired, seed it
  // now so the flow can't finalize without ever showing it.
  bindings.endOnboardingActive()
  seedLookAround(session)

  // NOTE: we do NOT clear the `?ob` recipe param here — the recipe still selects
  // the welcome wording on a mid-onboarding reload. ChatBox clears it once
  // onboarding ends (onboardingActive → false).

  // 2) Latch onboarding complete + refresh the session so the gate releases.
  // endOnboardingActive() (step 1) already flipped onboardingActive false, so
  // the completion-driven gate transition remounts ChatBox with
  // onboardingActive false and useChatOnboarding's start effect does not
  // re-run. The session is NO LONGER disposed here: it stays alive (bindings
  // keep repointing — the bind effect is not gated on onboardingActive) so the
  // letter poll below can seed the reveal letter when it lands; disposal moved
  // to the letter poll's terminal states (see startLetterPoll). The composer
  // unlock is driven by the context flip, not the latch — see MainLayout #3/#9.
  // (The rail is always visible now; it is not tied to this flip.)
  void (async () => {
    try {
      await onboarding.completeOnboardingSession(token)
    } catch (err) {
      console.warn('[useChatOnboarding] completeOnboardingSession failed:', err)
    }
    try {
      await bindings.refreshSession()
    } catch (err) {
      console.warn('[useChatOnboarding] session refresh failed:', err)
    }
  })()

  // 3) Surface the discovery reveal letter (#5226): poll discovery-status until
  // the background letter job is terminal, and seed the personalized
  // revealText as an onboarding-routed assistant message when it lands. This
  // also runs on the cap-timeout path — if mail lands late the letter can
  // still arrive within the poll window.
  startLetterPoll(session)
}

/**
 * Poll discovery-status for the reveal letter after finalize (#5226). Owns the
 * session's END OF LIFE: every exit — letter seeded, terminal-without-a-letter
 * (failed / deferred / empty-account fallback → honest silence), poll timeout,
 * or nothing to poll — disposes the session.
 *
 * Reload caveat: the poll lives in the module-level session, so it survives
 * ChatBox remounts but NOT a full page reload in the reveal→letter window
 * (onboarding is complete by then, so no new session ever starts). The letter
 * is persisted when seeded, and the content dedupe (hasOnboardingMessage +
 * seededOnboardingContent) keeps it at-most-once.
 */
function startLetterPoll(session: OnboardingSession): void {
  const { conversationId } = session
  if (!session.bindings.token || !session.bindings.workspaceId) {
    disposeSession(conversationId)
    return
  }
  // Hard stop: the letter normally lands seconds after the first mail sync
  // completes; past this window it is not coming this run.
  session.timers.push(
    setTimeout(() => disposeSession(conversationId), LETTER_POLL_TIMEOUT_MS),
  )
  session.poll = setInterval(() => {
    const live = sessions.get(conversationId)
    if (!live) return
    // Cross-workspace guard: the endpoint helpers resolve the workspace from
    // getActiveWorkspaceId() at CALL time, so if the user switched workspaces
    // mid-window a tick here would query the WRONG tenant — and its terminal
    // letter (failed/deferred/empty) would dispose THIS session, silently
    // losing the real letter. Skip the tick; the timeout still bounds the poll.
    if (getActiveWorkspaceId() !== live.bindings.workspaceId) return
    onboarding
      .discoveryStatus(live.bindings.token)
      .then((status) => handleLetterStatus(conversationId, status))
      .catch(() => {
        // Transient discovery-status errors are non-fatal — the timeout bounds the poll.
      })
  }, POLL_INTERVAL_MS)
}

function handleLetterStatus(
  conversationId: string,
  status: DiscoveryStatus | null | undefined,
): void {
  const session = sessions.get(conversationId)
  if (!session) return
  const letter = status?.jobs?.letter
  if (!letter) return
  if (letter.status === 'failed' || letter.status === 'deferred') {
    // Terminal without a letter (empty_account / dependency_failed / crash):
    // seed NOTHING — honest silence (#5226).
    disposeSession(conversationId)
    return
  }
  if (letter.status !== 'done') return // pending/running — keep polling
  const text = typeof letter.revealText === 'string' ? letter.revealText.trim() : ''
  // 'done' carrying the empty-account fallback (empty:true / blank text) also
  // stays silent — the "nothing to read yet" placeholder is not a reveal.
  if (!text || letter.empty === true) {
    disposeSession(conversationId)
    return
  }
  // Hold the seed while a different thread is on screen (see
  // getActiveConversationId) — keep polling; we seed the moment the welcome
  // thread is back, or the poll timeout ends the wait.
  const active = session.bindings.getActiveConversationId()
  if (active && active !== conversationId) return
  if (!session.letterSeeded) {
    session.letterSeeded = true
    seedOnboardingMessage(session, text)
  }
  disposeSession(conversationId)
}

/**
 * Start the orchestration for `conversationId` exactly once. Idempotent: if a
 * session already exists (started on a prior ChatBox mount), this only refreshes
 * its bindings and returns — it does NOT re-seed or reschedule.
 */
function startOnboardingSession(
  conversationId: string,
  bindings: OnboardingBindings,
): void {
  const existing = sessions.get(conversationId)
  if (existing) {
    // Remount: repoint at the current mount's fresh callbacks. The already-
    // scheduled timers/poll keep running and will call these new refs. There are
    // no look-around pills to re-apply (onboarding is welcome + invite only).
    existing.bindings = bindings
    return
  }

  const session: OnboardingSession = {
    conversationId,
    bindings,
    welcomeSeeded: false,
    lookAroundSeeded: false,
    finalized: false,
    floorElapsed: false,
    syncReady: false,
    letterSeeded: false,
    timers: [],
    poll: null,
  }
  sessions.set(conversationId, session)

  // ── Seed the welcome line once for the whole run. The per-session
  // welcomeSeeded flag keeps the seed logic to one run per session; the
  // synchronous cross-session guard inside seedOnboardingMessage is what stops a
  // second session (conversationId change at start) from duplicating it. ──
  if (!session.welcomeSeeded) {
    session.welcomeSeeded = true
    seedOnboardingMessage(session, session.bindings.recipe.welcome)
  }

  // ── Look-around invite after a beat (#5387) — display-only, does NOT wait on
  // sync readiness. Reads session.bindings at fire time so a remount that
  // repointed bindings is honored. ──
  session.timers.push(
    setTimeout(() => {
      const live = sessions.get(conversationId)
      if (!live || live.finalized) return
      seedLookAround(live)
    }, SECOND_MESSAGE_DELAY_MS),
  )

  // ── Poll sync readiness. The reveal is gated on floor AND readiness, with
  // the cap as the never-trap escape (#5225). ──
  if (bindings.token && bindings.workspaceId) {
    session.poll = setInterval(() => {
      const live = sessions.get(conversationId)
      if (!live || live.finalized) return
      // Cross-workspace guard — see the letter poll in startLetterPoll: the
      // endpoint resolves the workspace at call time, so a tick after a
      // mid-window workspace switch would read the WRONG tenant's counts.
      if (getActiveWorkspaceId() !== live.bindings.workspaceId) return
      const { token } = live.bindings
      onboarding
        .syncStatus(token)
        .then((status) => {
          const stillLive = sessions.get(conversationId)
          if (!stillLive || stillLive.finalized) return
          if (status?.isReady) {
            stillLive.syncReady = true
            maybeFinalize(stillLive)
          }
        })
        .catch(() => {
          // Transient sync-status errors are non-fatal — the cap still
          // advances the flow.
        })
    }, POLL_INTERVAL_MS)
  } else {
    // Nothing to poll — readiness can never be observed, so treat it as ready
    // and let the floor alone reveal (the pre-#5225 floor-only behavior).
    session.syncReady = true
  }

  // Floor: the earliest the reveal may fire. Ready already → reveal here;
  // otherwise wait for readiness (or the cap). No status line is shown while
  // waiting — background sync is silent by design.
  session.timers.push(
    setTimeout(() => {
      const live = sessions.get(conversationId)
      if (!live || live.finalized) return
      live.floorElapsed = true
      if (live.syncReady) {
        finalize(live)
      }
    }, REVEAL_FLOOR_MS),
  )
  // Cap: never trap the user — reveal even if readiness never arrives.
  session.timers.push(
    setTimeout(() => {
      const live = sessions.get(conversationId)
      if (!live || live.finalized) return
      live.floorElapsed = true
      finalize(live)
    }, REVEAL_CAP_MS),
  )
}

interface UseChatOnboardingArgs {
  /** True while chat-window onboarding should run (from OnboardingActiveContext). */
  onboardingActive: boolean
  /** The active conversation id (the auto-created "Welcome" thread), or undefined. */
  conversationId: string | undefined
  /**
   * True once the active thread's persisted messages have finished loading
   * (ChatBox passes `!isLoading`). The session must not START before this: the
   * welcome line is seeded synchronously, and on a hard reload mid-onboarding the
   * persisted welcome is still in flight — seeding before it lands makes
   * `hasOnboardingMessage` read an empty array and duplicate the welcome (#4598
   * follow-up). Gating start on a loaded thread lets the idempotency check see the
   * already-persisted line; a brand-new thread loads empty in one tick and seeds
   * normally.
   */
  messagesReady: boolean
  token: string | null
  /** Resolved active workspace id (for the sync-status poll). */
  workspaceId: string
  /**
   * Context-selected onboarding recipe (welcome + invite copy). Resolved in
   * ChatBox from the `?ob` URL param / signed-in email domain and threaded down;
   * the seed steps read it so the welcome/look-around lines are recipe-specific.
   * Defaults to the `default` recipe.
   */
  recipe: OnboardingRecipe
  /** Inject a synthetic assistant ChatMessage into the real thread (persists too). */
  addAssistantMessage: (
    content: string,
    conversationId?: string,
    metadata?: { routing?: string; durationMs?: number; intent?: string },
  ) => void
  /**
   * Predicate over the CURRENTLY-LOADED thread: true if an onboarding-routed
   * assistant message with exactly `content` is already present. Threaded from
   * ChatBox (which holds the `messages` array from useChat) so seeding is
   * idempotent against the real thread — an HMR reset or hard reload that lost
   * the session registry but reloaded the persisted messages won't duplicate
   * them.
   */
  hasOnboardingMessage: (content: string) => boolean
  /** Flip onboardingActive → false (reveals the sidebar). */
  endOnboardingActive: () => void
  /** Refresh the session so profile.onboardingComplete flips true. */
  refreshSession: () => Promise<void> | void
  /**
   * Ref-backed accessor for the conversation currently on screen, so the
   * post-reveal letter seed can hold while a different thread is active (#5226).
   */
  getActiveConversationId: () => string | null | undefined
}

export function useChatOnboarding({
  onboardingActive,
  conversationId,
  messagesReady,
  token,
  workspaceId,
  recipe,
  addAssistantMessage,
  hasOnboardingMessage,
  endOnboardingActive,
  refreshSession,
  getActiveConversationId,
}: UseChatOnboardingArgs): void {
  // The latest callbacks/values, mirrored into a ref so the bind effect can
  // hand the singleton fresh closures on every render without re-running the
  // start logic. (The singleton calls through `session.bindings`, which we
  // repoint here.)
  const bindingsRef = useRef<OnboardingBindings>({
    token,
    workspaceId,
    recipe,
    addAssistantMessage,
    hasOnboardingMessage,
    endOnboardingActive,
    refreshSession,
    getActiveConversationId,
  })
  bindingsRef.current = {
    token,
    workspaceId,
    recipe,
    addAssistantMessage,
    hasOnboardingMessage,
    endOnboardingActive,
    refreshSession,
    getActiveConversationId,
  }

  // Keep a live session's bindings current on every render (remount or
  // re-render), so timers/polls scheduled on a prior mount always invoke THIS
  // mount's callbacks — never a stale closure.
  useEffect(() => {
    if (!conversationId) return
    const live = sessions.get(conversationId)
    if (live) live.bindings = bindingsRef.current
  })

  // Start the orchestration once per onboarding conversation. Guarded by the
  // module-level registry (startOnboardingSession), NOT by a per-mount ref — so
  // a ChatBox remount with onboardingActive still true re-binds the existing
  // session rather than starting a second one or re-seeding "Welcome".
  useEffect(() => {
    if (!onboardingActive) return
    if (!conversationId) return
    // Wait for the persisted thread to load before seeding — see messagesReady.
    // On a brand-new thread this is true within a tick (empty load); on a reload
    // it holds the synchronous welcome seed until hasOnboardingMessage can see the
    // already-persisted welcome, so we don't duplicate it.
    if (!messagesReady) return
    startOnboardingSession(conversationId, bindingsRef.current)
    // Intentionally NO cleanup: the session must outlive ChatBox unmounts. It is
    // disposed only when the post-reveal letter poll terminates (see
    // startLetterPoll → disposeSession).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingActive, conversationId, messagesReady])
}

/**
 * Test-only: reset the module-level session registry between cases so the
 * one-shot module state doesn't leak across tests.
 */
export function __resetOnboardingSessionsForTest(): void {
  for (const id of [...sessions.keys()]) disposeSession(id)
  seededOnboardingContent.clear()
}
